import React, { useRef, useState, useEffect } from "react";
import { FaMapMarkerAlt, FaCheck } from "react-icons/fa";
import { motion } from "framer-motion";
import GradientGlowDivider from "../../Components/GradientGlowDivider";
import { useNavigate, useParams } from "react-router-dom";
import Field from "../../Components/Field";
import WhiteButton from "../../Components/WhiteButton";
import TextAreaField from "../../Components/TextArea";
import Toast from "../../Components/Toast";
import { openingRoles } from "../../OpeningRolesData";
import Loader from "../../Components/Loader";

const CareerFormPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const applyingRole = openingRoles.find((item) => item.id === parseInt(id));

  // Upload file to Google Drive via Netlify function
  const handleUpload = async () => {
    if (!selectedFile) return null;

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const res = await fetch(
        "https://datapixl.com/.netlify/functions/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      const body = await res.text();
      if (res.status === 200) return body;
      return null;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    if (!applyingRole) {
      // Redirect if ID is not valid
      navigate("/");
    }
  }, [applyingRole, navigate]);

  useEffect(() => {
    // Wait a bit for the animation to finish
    const scrollTimeout = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" }); // Smooth scroll
    }, 100); // Adjust this delay as needed

    return () => clearTimeout(scrollTimeout); // Cleanup timeout on unmount
  }, []);

  const [showToast, setShowToast] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);

  const triggerToast = () => {
    setShowToast(true);
    if (timeoutId) clearTimeout(timeoutId);
    const newTimeout = setTimeout(() => {
      setShowToast(false);
      setTimeoutId(null);
    }, 3000);
    setTimeoutId(newTimeout);
  };

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    interest: "",
    otherInfo: "",
    experience: "",
    file: null,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      let formatted = value;
      if (formatted.startsWith("+")) {
        formatted = "+" + formatted.slice(1).replace(/\D/g, "");
      } else {
        formatted = formatted.replace(/\D/g, "");
      }
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full Name is required";

    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email))
      newErrors.email = "Invalid email address";

    if (!/^\+?\d{10,15}$/.test(formData.phone))
      newErrors.phone = "Enter a valid phone number (10–15 digits)";

    if (!formData.experience)
      newErrors.experience = "Please select your experience";

    if (!formData.file) {
      newErrors.file = "Please upload a file";
    } else if (formData.file.size > 2 * 1024 * 1024) {
      newErrors.file = "File must be 2MB or smaller";
    }

    return newErrors;
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file && file.size > 2 * 1024 * 1024) {
      alert("File must be 2MB or smaller.");
      fileInputRef.current.value = "";
      setSelectedFile(null);
      setFileName("");
      setFormData((prev) => ({ ...prev, file: null }));
      return;
    }

    setSelectedFile(file);
    setFileName(file ? file.name : "");
    setFormData((prev) => ({ ...prev, file }));
  };

  const experienceMap = {
    oneToThreeYears: "1 to 3 years",
    threeToFiveYears: "3 to 5 years",
    moreThanFiveYears: "More than 5 years",
    notApplicable: "Not Applicable",
  };

  const experienceOptions = [
    { label: "1 to 3 years", name: "oneToThreeYears" },
    { label: "3 to 5 years", name: "threeToFiveYears" },
    { label: "More than 5 years", name: "moreThanFiveYears" },
    { label: "Not Applicable", name: "notApplicable" },
  ];

  const [checkboxStates, setCheckboxStates] = useState({
    oneToThreeYears: false,
    threeToFiveYears: false,
    moreThanFiveYears: false,
    notApplicable: false,
  });

  const handleCheckboxChange = (event) => {
    const { name } = event.target;
    setCheckboxStates({
      oneToThreeYears: false,
      threeToFiveYears: false,
      moreThanFiveYears: false,
      notApplicable: false,
      [name]: true,
    });
    setFormData((prev) => ({
      ...prev,
      experience: experienceMap[name],
    }));
  };

  const flattenObject = (obj, prefix = "") => {
    let result = {};
    for (const [key, value] of Object.entries(obj)) {
      const newKey = prefix ? `${prefix}[${key}]` : key;
      if (typeof value === "object" && value !== null) {
        Object.assign(result, flattenObject(value, newKey));
      } else {
        result[newKey] = value;
      }
    }
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setIsLoading(false);
      return;
    }

    const allData = {
      ...applyingRole,
      ...formData,
    };

    const flatData = flattenObject(allData);
    try {
      const uploadedFileURL = await handleUpload(selectedFile);
      flatData["resume"] = uploadedFileURL;
      await fetch("/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          "form-name": "career-form",
          ...flatData,
        }).toString(),
      });

      triggerToast();

      // Reset all state after successful submission
      setFormData({
        name: "",
        phone: "",
        email: "",
        interest: "",
        otherInfo: "",
        experience: "",
        file: null,
      });

      setCheckboxStates({
        oneToThreeYears: false,
        threeToFiveYears: false,
        moreThanFiveYears: false,
        notApplicable: false,
      });

      setSelectedFile(null);
      setFileName("");

      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    fileInputRef.current.click();
  };

  if (!applyingRole) return null;
  return (
    <motion.div>
      <section className="md:w-3/5 mx-auto py-14 px-6 md:px-0 md:py-[6%]">
        <div className="flex justify-end mb-8 md:mb-10">
          <button
            onClick={() => navigate(-1)}
            className="w-6 h-6 md:w-12 md:h-12 flex items-center justify-center cursor-pointer"
            aria-label="Close"
          >
            <img src="/icons/Close.svg" alt="Close" className="w-full h-full" />
          </button>
        </div>
        <h1 className="mb-2 md:mb-3 font-bold text-[24px] leading-[140%] tracking-normal md:text-[48px]">
          {applyingRole.title}
        </h1>
        <div className="flex flex-row items-center space-x-1 md:space-x-2">
          <FaMapMarkerAlt className="h-3.5 w-3.5 md:h-7 md:w-7" color="white" />
          <h1 className="font-medium text-[12px] leading-[140%] tracking-normal md:text-[24px]">
            {applyingRole.location}
          </h1>
        </div>

        <GradientGlowDivider className="my-7 md:my-10" />

        <form
          onSubmit={handleSubmit}
          name="career-form"
          method="POST"
          data-netlify="false"
          data-netlify-honeypot="bot-field"
          encType="multipart/form-data"
          netlify
        >
          {/* Hidden input to tell Netlify the form name */}

          <input
            type="hidden"
            name="form-name"
            value="career-form"
            className="hidden"
          />
          <input type="hidden" name="bot-field" className="hidden" />

          <div className="flex flex-col space-y-12 md:space-y-20">
            <div>
              <label className="block career-form-field-title" htmlFor="name">
                Full Name
              </label>
              <Field
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <p className="career-form-field-error">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block career-form-field-title" htmlFor="phone">
                Phone Number
              </label>
              <Field
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
              {errors.phone && (
                <p className="career-form-field-error">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block career-form-field-title" htmlFor="email">
                Email Address
              </label>
              <Field
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <p className="career-form-field-error">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block career-form-field-title" htmlFor="resume">
                CV / Resume
              </label>

              <button
                type="button"
                onClick={handleClick}
                className="border border-[#7D7D7D] bg-white rounded-[12px] px-2 py-1.5 md:px-4 md:py-2 text-black font-[500] text-[14px] md:text-[20px] leading-[140%]"
              >
                Browse........
              </button>
              <span className="ms-3 text-white text-[14px] md:text-[16px] font-medium">
                {fileName || "No Selected File"}
              </span>
              <input
                type="file"
                accept="application/pdf"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                name="resume"
              />
              {errors.file && (
                <p className="career-form-field-error">{errors.file}</p>
              )}
              <div className="h-16 md:h-20"></div>
            </div>
          </div>

          <div className="hidden md:block">
            <label className="block career-form-field-title">
              How many years of experience do you have
              {applyingRole.generalRole ? "?" : " in this role?"}
            </label>
            <div className="space-y-3">
              {experienceOptions.map(({ label, name }, idx) => (
                <label key={idx} className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name={name}
                      checked={checkboxStates[name]}
                      onChange={handleCheckboxChange}
                      className="peer appearance-none w-3.5 h-3.5 md:w-6 md:h-6 border-2 border-white rounded-sm bg-transparent checked:bg-white checked:border-white"
                    />
                    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <FaCheck
                        className={`w-3 h-3 text-black ${
                          checkboxStates[name] ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </span>
                  </label>
                  <span className="font-bold text-[14px] md:text-[22px] leading-[140%] tracking-[0] text-white">
                    {label}
                  </span>
                </label>
              ))}
            </div>
            {errors.experience && (
              <p className="career-form-field-error">{errors.experience}</p>
            )}
          </div>

          <div className="md:mt-20">
            <label className="block career-form-field-title" htmlFor="interest">
              Tell us why you’re interested in working with us?
            </label>
            <TextAreaField
              id="interest"
              name="interest"
              onChange={handleChange}
              value={formData.interest}
            />
          </div>

          <div className="md:mt-20">
            <label
              className="block career-form-field-title mt-5"
              htmlFor="otherInfo"
            >
              Anything else you want us to know?
            </label>
            <TextAreaField
              id="otherInfo"
              name="otherInfo"
              value={formData.otherInfo}
              onChange={handleChange}
            />
          </div>

          <div className="mt-5 md:hidden">
            <label className="block career-form-field-title">
              How many years of experience do you have
              {applyingRole.generalRole ? "?" : " in this role?"}
            </label>
            <div className="space-y-3">
              {experienceOptions.map(({ label, name }, idx) => (
                <label key={idx} className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name={name}
                      checked={checkboxStates[name]}
                      onChange={handleCheckboxChange}
                      className="peer appearance-none w-3.5 h-3.5 md:w-6 md:h-6 border-2 border-white rounded-sm bg-transparent checked:bg-white checked:border-white"
                    />
                    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <FaCheck
                        className={`w-3 h-3 text-black ${
                          checkboxStates[name] ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </span>
                  </label>
                  <span className="font-bold text-[14px] md:text-[22px] leading-[140%] tracking-[0] text-white">
                    {label}
                  </span>
                </label>
              ))}
            </div>
            {errors.experience && (
              <p className="career-form-field-error">{errors.experience}</p>
            )}
          </div>

          <div className="flex mt-14 md:mt-20">
            {isLoading ? (
              <Loader />
            ) : (
              <WhiteButton
                type="submit"
                text="Submit your Application"
                onClick={() => {}}
              />
            )}
          </div>
        </form>

        <Toast
          show={showToast}
          message="Your application has been submitted successfully!"
          onClose={() => {
            setShowToast(false);
            if (timeoutId) clearTimeout(timeoutId);
          }}
        />
      </section>
    </motion.div>
  );
};

export default CareerFormPage;
